<template>
  <div v-if="domain && consumption">
    <RouterLink
      :to="{
        name: 'ComplianceGroup',
        params: { group: domain.group },
        query: { view: 'outgoing' },
      }"
      class="btn btn-sm btn-outline-secondary mb-3"
    >
      ← Consolidated view
    </RouterLink>
    <div class="mb-4">
      <h1 class="cg-page-title mb-1">{{ domain.name }}</h1>
      <p class="mb-0 text-body-secondary small">
        {{ domain.isMonthly ? "Monthly matching" : "Annual matching" }} ·
        {{
          domain.supplierRequired
            ? "Cancellation needs supplier coordination"
            : "Direct via Soldera"
        }}
      </p>
    </div>

    <div
      v-if="domain.key === 'fr' && short"
      class="d-flex align-items-center justify-content-between gap-3 mb-4 p-3 border rounded small"
      :class="
        store.state.requestSubmitted
          ? 'bg-body-secondary'
          : 'bg-warning-subtle border-warning-subtle text-warning-emphasis'
      "
    >
      <span v-if="!store.state.requestSubmitted"
        ><strong class="cg-tabular"
          >{{ short.label }} 2026 is short {{ fmt(short.gap) }} MWh</strong
        >
        · consumption {{ fmt(short.consumption) }} MWh, allocated
        {{ fmt(short.consumption - short.gap) }} MWh</span
      >
      <span v-else
        ><strong>Request pending.</strong> {{ short.label }} 2026 ·
        {{ fmt(short.gap) }} MWh requested from Soldera 10 Sep 2026</span
      >
      <RouterLink
        :to="{
          name: store.state.requestSubmitted
            ? 'ComplianceGroupRequestSubmitted'
            : 'ComplianceGroupSourceRequest',
          params: { group: 'eecs' },
        }"
        class="btn btn-sm text-nowrap"
        :class="
          store.state.requestSubmitted ? 'btn-outline-secondary' : 'btn-warning'
        "
      >
        {{
          store.state.requestSubmitted ? "View request" : "Source certificates"
        }}
      </RouterLink>
    </div>

    <TabsContainer
      :tabs="TABS"
      :activeTab="tab"
      @change:tab="tab = $event as DomainTab"
    >
      <template #tab-consumption>
        <ConsumptionOverview
          :domain
          :view="consumption"
          @open-month="openMonth"
          @report="reportOpen = true"
        />
      </template>
      <template #tab-portfolios>
        <PortfoliosTab :domain @open-month="openMonth" />
      </template>
      <template #tab-companies>
        <CompaniesTab :domain />
      </template>
    </TabsContainer>

    <ConsumptionReportModal
      v-if="reportOpen"
      :domain
      :view="consumption"
      @close="reportOpen = false"
    />
    <MonthDetailModal
      v-if="monthIndex !== null"
      :detail="deriveMonthDetail(domain, monthIndex)"
      :domainName="domain.name"
      :isMonthly="domain.isMonthly"
      @close="monthIndex = null"
      @resolve="goResolve"
    />
  </div>
</template>

<script setup lang="ts">
import TabsContainer from "@/components/common/tabs/TabsContainer.vue";
import { useQueryManager } from "@/composables/useQueryManager";
import type { Tab } from "@/composables/useTabs";
import { computed, ref, watch } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import CompaniesTab from "./components/CompaniesTab.vue";
import ConsumptionOverview from "./components/ConsumptionOverview.vue";
import ConsumptionReportModal from "./components/ConsumptionReportModal.vue";
import MonthDetailModal from "./components/MonthDetailModal.vue";
import PortfoliosTab from "./components/PortfoliosTab.vue";
import {
  DOMAIN_INPUTS,
  isDomainKey,
  isGroupKey,
} from "./fixture/complianceGroupsFixture";
import {
  deriveConsumption,
  deriveMonthDetail,
  franceShortMonth,
} from "./fixture/derive";
import { fmt } from "./fixture/format";
import { useComplianceDemoStore } from "./useComplianceDemoStore";

type DomainTab = "consumption" | "portfolios" | "companies";
const TABS: Tab[] = [
  { id: "consumption", name: "Consumption" },
  { id: "portfolios", name: "Portfolios" },
  { id: "companies", name: "Companies" },
];

const route = useRoute();
const router = useRouter();
const store = useComplianceDemoStore();
const { getQueryValue, parsers } = useQueryManager();

// A domain belongs to one group: unknown keys and cross-group URLs (rego/domains/fr) both go
// back to the overview of the group the domain actually belongs to.
watch(
  () => [route.params.group, route.params.domain] as const,
  ([group, domainParam]) => {
    if (domainParam === undefined) return;
    const owner = isDomainKey(domainParam)
      ? DOMAIN_INPUTS[domainParam].group
      : null;
    if (owner === null || owner !== group) {
      router.replace({
        name: "ComplianceGroup",
        params: { group: owner ?? (isGroupKey(group) ? group : "eecs") },
      });
    }
  },
  { immediate: true }
);
// Null until the URL names a domain of this group; the watcher above redirects meanwhile.
const domain = computed(() => {
  const key = route.params.domain;
  return isDomainKey(key) && DOMAIN_INPUTS[key].group === route.params.group
    ? store.domains[key]
    : null;
});
const short = computed(() => franceShortMonth(store.domains.fr));
const consumption = computed(
  () => domain.value && deriveConsumption(domain.value)
);

const tabQuery = getQueryValue(
  "tab",
  parsers.enum<DomainTab>(["consumption", "portfolios", "companies"])
);
const tab = computed<DomainTab>({
  get: () => tabQuery.value ?? "consumption",
  set: (v) => (tabQuery.value = v),
});
const reportOpen = ref(false);
const monthIndex = ref<number | null>(null);
const openMonth = (index: number) => (monthIndex.value = index);
const goResolve = () => {
  monthIndex.value = null;
  router.push({
    name: "ComplianceGroupSourceRequest",
    params: { group: "eecs" },
  });
};
</script>
